import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Modal,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DocumentContent from '../../../../../components/DocumentContent';
import SegmentItem from '../../../../../components/segmentItem';
import {formatSegmentsInDocumentsSummary} from './formatSegmentsInDocumentsSummary';

export default function DeltaStoryModal({
                                            open,
                                            onClose,
                                            selectedEntity,
                                            modalTitle,
                                            stories,
                                            activeStory,
                                            activeSegmentId,
                                            onOpenStory,
                                            onOpenRoot,
                                            documentContentProps,
                                            showDocumentLoading,
                                            activeSegmentIndexInDocument,
                                            documentViewMode,
                                            setDocumentViewMode,
                                        }) {
    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="delta-story-modal"
            slotProps={{
                backdrop: {
                    sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.55)',
                    },
                },
            }}
            sx={{
                padding: '24px',
            }}
        >
            <Stack sx={{bgcolor: 'white', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 2}}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{px: 2, py: 1.5}}
                >
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {modalTitle
                            || (selectedEntity?.label ? `${selectedEntity.label} delta stories` : 'Delta clusters')}
                    </Typography>
                    <IconButton aria-label="Close delta stories modal" size="small" onClick={onClose}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Stack>

                <Divider/>

                <Stack direction="row" flex={1} alignItems="stretch" overflow="hidden">
                    <Stack
                        sx={{
                            width: 360,
                            minWidth: 360,
                            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                            bgcolor: 'background.paper',
                        }}
                    >
                        {activeStory && (
                            <>
                                <Button
                                    startIcon={<ArrowBackIcon/>}
                                    onClick={onOpenRoot}
                                    sx={{alignSelf: 'flex-start', m: 1}}
                                >
                                    Back to stories
                                </Button>

                                <Typography variant="subtitle1" fontWeight={600} px={2}>
                                    {activeStory?.title}
                                </Typography>
                            </>
                        )}

                        <Box sx={{overflowY: 'auto', flex: 1, p: 1}}>
                            {!activeStory && (
                                <List dense sx={{p: 0}}>
                                    {stories.map((story) => (
                                        <ListItemButton
                                            key={story.clusterId}
                                            onClick={() => onOpenStory(story)}
                                            sx={{borderRadius: 1, mb: 0.5}}
                                        >
                                            <ListItemIcon sx={{minWidth: 34}}>
                                                <FolderOutlinedIcon fontSize="small"/>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={story.title || `Story ${story.clusterId}`}
                                                secondary={formatSegmentsInDocumentsSummary(story.segments)}
                                                primaryTypographyProps={{variant: 'body2'}}
                                                secondaryTypographyProps={{variant: 'caption'}}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            )}

                            {activeStory && (
                                <Stack spacing={2} p={1}>
                                    {(Array.isArray(activeStory.segments) ? activeStory.segments : []).map((segment) => (
                                        <SegmentItem
                                            key={segment.segmentId}
                                            props={{
                                                displayMode: 'delta-list',
                                                isSelected: String(activeSegmentId) === String(segment.segmentId),
                                                onSelectSegment: () => onOpenStory(activeStory, segment.segmentId),
                                                segmentType: 'search',
                                                docId: segment.docId,
                                                docTitle: segment.docTitle || `Doc ${segment.docId || segment.segmentId}`,
                                                content: segment.content || '',
                                                segmentId: segment.segmentId,
                                                index: segment.segmentIndex ?? 0,
                                                offset: segment.offset ?? 0,
                                                tags: [],
                                            }}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Stack>

                    <Stack flex={1} minWidth={0} sx={{bgcolor: 'background.paper', overflow: 'hidden'}}>
                        {!documentContentProps && (
                            <Stack alignItems="center" justifyContent="center" height="100%">
                                <Typography variant="body2" color="text.secondary">
                                    Select a segment to view content.
                                </Typography>
                            </Stack>
                        )}

                        {showDocumentLoading && (
                            <Stack alignItems="center" justifyContent="center" height="100%" spacing={1}>
                                <CircularProgress size={20}/>
                                <Typography variant="body2" color="text.secondary">
                                    Loading document...
                                </Typography>
                            </Stack>
                        )}

                        {!showDocumentLoading && documentContentProps && (
                            <Box sx={{height: '100%', overflowY: 'auto', minHeight: 0}}>
                                <DocumentContent
                                    props={documentContentProps}
                                    savedHighlights={[]}
                                    searchResultSegmentIds={[]}
                                    activeSegmentIndex={activeSegmentIndexInDocument}
                                    viewMode={documentViewMode}
                                    setViewMode={setDocumentViewMode}
                                    htmlSegments={null}
                                    loadingHtml={false}
                                />
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Modal>
    );
}

